import {
  Popover as HeadlessPopover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react'
import type {
  Modifier,
  Placement,
  PositioningStrategy,
} from '@ui-components/Tooltip'
import classNames from 'classnames'
import React, { MouseEventHandler, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePopper } from 'react-popper'

export interface IPopoverProps {
  buttonRef?: React.MutableRefObject<any>
  portalMode?: boolean
  button:
    | React.ReactNode
    | (({ open, close }: { open: boolean; close: Function }) => React.ReactNode)
  content:
    | React.ReactNode
    | (({
        updatePopper,
        close,
      }: {
        updatePopper: Function
        close: Function
      }) => React.ReactNode)
  popperPlacement?: Placement
  popperStrategy?: PositioningStrategy
  popperModifiers?: Partial<Modifier<unknown, object>>[]
  disabled?: boolean
  panelClassName?: string
  containerClassName?: string
  buttonClassName?: string
  onClick?: MouseEventHandler<HTMLDivElement>
}

const defaultModifiers = [
  {
    name: 'eventListeners',
    options: {
      scroll: false,
      resize: true,
    },
  },
]

const Popover: React.FunctionComponent<IPopoverProps> = ({
  buttonRef,
  button,
  portalMode = false,
  content,
  popperPlacement,
  popperStrategy,
  popperModifiers,
  disabled,
  panelClassName,
  containerClassName,
  buttonClassName,
  onClick,
}) => {
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>()
  const [popperElement, setPopperElement] = useState<HTMLElement>()
  const {
    styles,
    attributes,
    update: updatePopper,
  } = usePopper(referenceElement, popperElement, {
    placement: popperPlacement,
    strategy: popperStrategy,
    modifiers: [...defaultModifiers, ...(popperModifiers || [])],
  })
  const popoverPanel = ({ open, close }) => (
    <PopoverPanel
      ref={setPopperElement}
      style={styles.popper}
      {...attributes.popper}
      className={classNames(['z-20', panelClassName])}
    >
      {typeof content === 'function'
        ? content({ updatePopper, close })
        : content}
    </PopoverPanel>
  )
  return (
    <HeadlessPopover className={containerClassName} onClick={onClick}>
      {({ open, close }) => (
        <>
          <PopoverButton
            ref={(ref) => {
              setReferenceElement(ref)
              if (buttonRef?.current) {
                buttonRef.current = ref
              }
            }}
            as="div"
            disabled={disabled}
            className={buttonClassName}
          >
            {typeof button === 'function' ? button({ open, close }) : button}
          </PopoverButton>
          {portalMode &&
            open &&
            createPortal(popoverPanel({ open, close }), document.body)}
          {!portalMode && popoverPanel({ open, close })}
        </>
      )}
    </HeadlessPopover>
  )
}

export default Popover
